import { Injectable } from '@angular/core'

import { ComponentStore } from '@ngrx/component-store'
import { catchError, of, switchMap, tap, map, Observable } from 'rxjs'

import {
  AssessmentService,
  GetAssessmentOptions,
} from '../services/assessment/assesssment.service'
import { StorageService } from '../services/storage.service'
import { AssessmentExecutionType, TagMatchMode } from '../../_generated/graphql'

interface AssessmentState {
  assessmentId: string
}

@Injectable({
  providedIn: 'root',
})
export class AssessmentStore extends ComponentStore<AssessmentState> {
  constructor(
    private assessmentService: AssessmentService,
    private storageService: StorageService,
  ) {
    super({
      assessmentId: '',
    })
  }

  getSkillMaps() {
    return this.assessmentService.getSkillMaps()
  }

  getAccounts() {
    return this.assessmentService.getAccounts()
  }

  getAssessment(assessmentId: string, options?: GetAssessmentOptions) {
    return this.assessmentService.getAssessment(assessmentId, options)
  }

  searchAssessments(
    query: string,
    pagination: { limit: number; offset: number },
    sort?: { sortField: string; sortOrder: 'asc' | 'desc' },
    tagIds?: string[],
    reportToUserId: string | null = null,
    tagMatch?: TagMatchMode | null,
  ) {
    return this.assessmentService
      .searchAssessments(
        query,
        pagination,
        'PROGRESS_STATUSES',
        sort,
        false,
        tagIds,
        reportToUserId,
        tagMatch,
      )
      .pipe(
        catchError(() => {
          return of()
        }),
      )
  }

  /** テナントで利用可能なアセスメントタグマスターを取得する */
  getAssessmentTagMasters() {
    return this.assessmentService.getAssessmentTagMasters()
  }

  /** アセスメントへタグを追加する */
  addAssessmentTag(assessmentId: string, tagMasterId: string) {
    return this.assessmentService.addAssessmentTag(assessmentId, tagMasterId)
  }

  /** アセスメントからタグを削除する */
  removeAssessmentTag(assessmentId: string, tagMasterId: string) {
    return this.assessmentService.removeAssessmentTag(assessmentId, tagMasterId)
  }

  createAssessment(
    userId: string,
    businessMeetingName: string,
    accountName: string | null,
    recordDate: Date,
    fileName: string,
    skillMapId: string | null,
    phaseIds: string[],
    assessmentExecutionType: AssessmentExecutionType,
  ) {
    return this.assessmentService
      .createAssessment({
        userId,
        businessMeetingName,
        accountName,
        recordDate,
        fileName,
        skillMapId,
        phaseIds,
        assessmentExecutionType,
      })
      .pipe(tap(() => {}))
  }

  executeAssessment(
    userId: string,
    orgId: string,
    businessMeetingName: string,
    accountName: string,
    recordDate: Date,
    fileName: string,
    skillMapId: string | null,
    phaseIds: string[],
    file: File,
    assessmentExecutionType: AssessmentExecutionType,
  ): Observable<string | undefined> {
    return this.createAssessment(
      userId,
      businessMeetingName,
      accountName,
      recordDate,
      fileName.replace(/\.json$/i, '.txt'),
      skillMapId,
      phaseIds,
      assessmentExecutionType,
    ).pipe(
      switchMap((res) => {
        return this.uploadFile(
          file,
          `${orgId}/${userId}/${res.data?.createAssessment}/${file.name}`,
        ).pipe(map(() => res.data?.createAssessment))
      }),
    )
  }

  uploadFile(file: File, filePath: string) {
    return this.storageService.uploadFile(file, filePath)
  }

  markContentAsAccessed(recommendedContentId: string) {
    return this.assessmentService.markContentAsAccessed(recommendedContentId)
  }

  exportAssessments(
    pagination?: { limit: number; offset: number },
    param?: { startDate: Date | null; endDate: Date | null },
  ) {
    return this.assessmentService.exportAssessments(pagination, param)
  }

  exportAssessmentSummaries(
    pagination?: { limit: number; offset: number },
    param?: {
      startDate: Date | null
      endDate: Date | null
    },
  ) {
    return this.assessmentService.exportAssessmentSummaries(pagination, param)
  }

  deleteAssessment(assessmentId: string) {
    return this.assessmentService.deleteAssessment(assessmentId)
  }

  deleteAssessments(assessmentIds: string[]) {
    return this.assessmentService.deleteAssessments(assessmentIds)
  }
}
